import { IError } from "../types/errors.d";
import { ActionType } from "../types/store-types";

export enum UserRoles {
    ADMIN,
    MODERATOR,
    USER,
}

export enum IUserTypes {
    GUEST = "GUEST",
    USER = "USER",
}

export interface IUser {
    _id: string;
    name: string;
    email: string;
    isActive: boolean;
    role: UserRoles;
    createdAt: string;
    updatedAt: string;
}

/* - Data Types - */

/* -- Init user Request Data */
export interface IInitUserRequestData {}

export interface IInitUserSuccessData {
    token: string;
    refresh_token: string;
    userType: IUserTypes;
}

export interface IInitUserFailureData extends IError {}

/* -- Forgot Password  Request Data */

export interface IForgotPasswordRequestData {
    email: string;
    redirect_url: string;
}

export interface IForgotPasswordSuccessData {}

export interface IForgotPasswordFailureData extends IError {}

/* -- Sign In user Request Data */

export interface ISignInRequestData {
    email: string;
    password: string;
}

export interface ISignInSuccessData {
    token: string;
    refresh_token: string;
    userType: IUserTypes;
}

export interface ISignInFailureData extends IError {}

/* -- Sign Up user Request Data */

export interface ISignUpRequestData {
    first_name: string;
    last_name: string;
    country: string;
    phone: string;
    email: string;
    password: string;
    redirect_url: string;
}

export interface ISignUpSuccessData extends IUser {}

export interface ISignUpFailureData extends IError {}

/* -- Confirm user Request Data */

export interface IConfirmUserRequestData {
    code: string;
}

export interface IConfirmUserSuccessData {}

export interface IConfirmUserFailureData extends IError {}

export interface ISetUserToken {
    token: string;
}

export interface ISetLoadingData {
    isLoading: boolean;
}

export interface IClearErrorsData {}

export interface ISetRedirectUrl {
    redirectUrl: string;
    redirectUrlQueries: string;
    isOAuth: boolean;
}

/* Oauth */

interface IOAuthRequest {
    email: string;
    password: string;
    client_id: string;
    redirect_url: string;
    state?: string;
    scope?: string;
}
interface IOAuthSuccess {
    url: string;
}

interface IOAuthFailure extends IError {}

/* Google */
interface IGoogleLogin {
    access_token: string;
    token_type: string;
}
interface IGoogleLoginSuccess {
    access_token: string;
    refresh_token: string;
}
interface IGoogleLoginFailure extends IError {}

/* - State - */

export interface IAuthState {
    user: IUser | null;
    userType: IUserTypes;
    token: string | null;
    refresh_token: string | null;
    errors: null | IError;
    isLoading: boolean;
    redirectUrl: string | null;
    redirectUrlQueries: string | null;
    isOAuth: boolean;
}

/* - Action Types - */

export enum AuthActionTypes {
    INIT_USER_REQUEST = "AUTH.INIT_USER_REQUEST",
    INIT_USER_SUCCESS = "AUTH.INIT_USER_SUCCESS",
    INIT_USER_FAILURE = "AUTH.INIT_USER_FAILURE",

    SIGN_IN_REQUEST = "AUTH.SIGN_IN_REQUEST",
    SIGN_IN_SUCCESS = "AUTH.SIGN_IN_SUCCESS",
    SIGN_IN_FAILURE = "AUTH.SIGN_IN_FAILURE",

    SIGN_UP_REQUEST = "AUTH.SIGN_UP_REQUEST",
    SIGN_UP_SUCCESS = "AUTH.SIGN_UP_SUCCESS",
    SIGN_UP_FAILURE = "AUTH.SIGN_UP_FAILURE",

    CONFIRM_USER_REQUEST = "AUTH.CONFIRM_USER_REQUEST",
    CONFIRM_USER_SUCCESS = "AUTH.CONFIRM_USER_SUCCESS",
    CONFIRM_USER_FAILURE = "AUTH.CONFIRM_USER_FAILURE",

    SET_USER_TOKEN = "AUTH.SET_USER_TOKEN",
    SET_LOADING = "AUTH.SET_LOADING",
    CLEAR_ERRORS = "AUTH.CLEAR_ERRORS",

    SET_REDIRECT_URL = "AUTH.SET_REDIRECT_URL",

    OATUH_FLOW_REQUEST = "AUTH.OAUTH_FLOW_REQUEST",
    OATUH_FLOW_SUCCESS = "AUTH.OAUTH_FLOW_SUCCESS",
    OATUH_FLOW_FAILURE = "AUTH.OAUTH_FLOW_FAILURE",

    FORGOT_PASSWORD_REQUEST = "AUTH.FORGOT_PASSWORD_REQUEST",
    FORGOT_PASSWORD_SUCCESS = "AUTH.FORGOT_PASSWORD_SUCCESS",
    FORGOT_PASSWORD_FAILURE = "AUTH.FORGOT_PASSWORD_FAILURE",

    /* Google */
    GOOGLE_LOGIN_REQUEST = "AUTH.GOOGLE_LOGIN_REQUEST",
    GOOGLE_LOGIN_SUCCESS = "AUTH.GOOGLE_LOGIN_SUCCESS",
    GOOGLE_LOGIN_FAILURE = "AUTH.GOOGLE_LOGIN_FAILURE",
}

/* - Action Interfaces  - */

export type InitUserRequestActionType = ActionType<AuthActionTypes.INIT_USER_REQUEST, IInitUserRequestData>;
export type InitUserSuccessActionType = ActionType<AuthActionTypes.INIT_USER_SUCCESS, IInitUserSuccessData>;
export type InitUserFailureActionType = ActionType<AuthActionTypes.INIT_USER_FAILURE, IInitUserFailureData>;

export type SignInRequestActionType = ActionType<AuthActionTypes.SIGN_IN_REQUEST, ISignInRequestData>;
export type SignInSuccessActionType = ActionType<AuthActionTypes.SIGN_IN_SUCCESS, ISignInSuccessData>;
export type SignInFailureActionType = ActionType<AuthActionTypes.SIGN_IN_FAILURE, ISignInFailureData>;

export type SignUpRequestActionType = ActionType<AuthActionTypes.SIGN_UP_REQUEST, ISignUpRequestData>;
export type SignUpSuccessActionType = ActionType<AuthActionTypes.SIGN_UP_SUCCESS, ISignUpSuccessData>;
export type SignUpFailureActionType = ActionType<AuthActionTypes.SIGN_UP_FAILURE, ISignUpFailureData>;

export type ConfirmUserRequestActionType = ActionType<AuthActionTypes.CONFIRM_USER_REQUEST, IConfirmUserRequestData>;
export type ConfirmUserSuccessActionType = ActionType<AuthActionTypes.CONFIRM_USER_SUCCESS, IConfirmUserSuccessData>;
export type ConfirmUserFailureActionType = ActionType<AuthActionTypes.CONFIRM_USER_FAILURE, IConfirmUserFailureData>;

export type SetUserTokenActionType = ActionType<AuthActionTypes.SET_USER_TOKEN, ISetUserToken>;
export type SetLoadingActionType = ActionType<AuthActionTypes.SET_LOADING, ISetLoadingData>;
export type ClearErrorsActionType = ActionType<AuthActionTypes.CLEAR_ERRORS, IClearErrorsData>;

export type SetRedirectUrlActionType = ActionType<AuthActionTypes.SET_REDIRECT_URL, ISetRedirectUrl>;

export type ForgotPasswordRequestActionType = ActionType<
    AuthActionTypes.FORGOT_PASSWORD_REQUEST,
    IForgotPasswordRequestData
>;

export type ForgotPasswordSuccessActionType = ActionType<
    AuthActionTypes.FORGOT_PASSWORD_SUCCESS,
    IForgotPasswordSuccessData
>;
export type ForgotPasswordFailureActionType = ActionType<
    AuthActionTypes.FORGOT_PASSWORD_FAILURE,
    IForgotPasswordFailureData
>;

export type GoogleLoginRequestActionType = ActionType<AuthActionTypes.GOOGLE_LOGIN_REQUEST, IGoogleLogin>;
export type GoogleLoginSuccessActionType = ActionType<AuthActionTypes.GOOGLE_LOGIN_SUCCESS, IGoogleLoginSuccess>;
export type GoogleLoginFailureActionType = ActionType<AuthActionTypes.GOOGLE_LOGIN_FAILURE, IGoogleLoginFailure>;

export type OAuthFlowRequestActionType = ActionType<AuthActionTypes.OATUH_FLOW_REQUEST, IOAuthRequest>;
export type OAuthFlowSuccessActionType = ActionType<AuthActionTypes.OATUH_FLOW_SUCCESS, IOAuthSuccess>;
export type OAuthFlowFailureActionType = ActionType<AuthActionTypes.OATUH_FLOW_FAILURE, IOAuthFailure>;
